import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import {
  clearAuth,
  getStoredToken,
  getStoredUser,
  loginUser,
  logoutUser,
  registerUser,
  storeAuth,
} from '../api'

type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'authenticated'; userId: string; token: string }

type AuthContextValue = {
  state: AuthState
  login: (userId: string, password: string) => Promise<void>
  register: (userId: string) => Promise<string>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' })

  // Restore session from localStorage on mount
  useEffect(() => {
    const token = getStoredToken()
    const userId = getStoredUser()
    if (token && userId) {
      setState({ status: 'authenticated', userId, token })
    } else {
      setState({ status: 'unauthenticated' })
    }
  }, [])

  const login = useCallback(async (userId: string, password: string) => {
    const res = await loginUser(userId, password)
    storeAuth(res.user_id, res.token)
    setState({ status: 'authenticated', userId: res.user_id, token: res.token })
  }, [])

  const register = useCallback(async (userId: string): Promise<string> => {
    const res = await registerUser(userId)
    return res.password
  }, [])

  const logout = useCallback(async () => {
    try {
      await logoutUser()
    } catch {
      // Swallow — clear local state regardless
    }
    clearAuth()
    setState({ status: 'unauthenticated' })
  }, [])

  return (
    <AuthContext.Provider value={{ state, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
